import MapIcon from "@mui/icons-material/Map";
import {
  Box,
  Button,
  Modal,
  ModalClose,
  Sheet,
  Typography,
  useColorScheme,
} from "@mui/joy";
import { useState } from "react";
import { hatchlingLocations } from "../../data/hatchlingData";

interface HatchlingLocationNameProps {
  id: string;
  children: React.ReactNode;
}

const HatchlingLocationName = ({
  id,
  children,
}: HatchlingLocationNameProps) => {
  const [open, setOpen] = useState(false);
  const { mode } = useColorScheme();
  const isDark = mode === "dark";

  const location = hatchlingLocations[id];

  if (!location) {
    return <>{children}</>;
  }

  const handleClick = () => {
    setOpen(true);
  };

  const handleViewOnMap = () => {
    setOpen(false);
    // Open the map page with this location selected
    let url = "/maps";
    if (location.mapCoordinates) {
      url = `/maps?location=${id}`;
    }
    window.open(url, "_blank");
  };

  return (
    <>
      <Typography
        component="span"
        onClick={handleClick}
        sx={{
          color: isDark ? "warning.300" : "warning.600",
          cursor: "pointer",
          fontWeight: "500",
          textDecoration: "none",
          position: "relative",
          "&:after": {
            content: '""',
            position: "absolute",
            width: "100%",
            height: "1px",
            bottom: 0,
            left: 0,
            backgroundColor: isDark ? "warning.300" : "warning.600",
            opacity: 0,
            transition: "opacity 0.2s",
          },
          "&:hover": {
            color: isDark ? "warning.200" : "warning.700",
            "&:after": {
              opacity: 0.5,
            },
          },
        }}
      >
        {children}
      </Typography>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Sheet
          variant="outlined"
          sx={{
            maxWidth: 480,
            borderRadius: "lg",
            boxShadow: "lg",
            position: "relative",
            border: "1px solid",
            borderColor: isDark ? "neutral.700" : "neutral.300",
            overflow: "hidden",
          }}
        >
          <ModalClose
            variant="plain"
            sx={{
              m: 1,
              zIndex: 10,
              bgcolor: isDark ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.8)",
              borderRadius: "50%",
            }}
          />

          {/* 16:9 Image Header */}
          <Box
            sx={{
              width: "100%",
              aspectRatio: "16/9",
              position: "relative",
              bgcolor: isDark ? "warning.900" : "warning.100",
              overflow: "hidden",
            }}
          >
            {location.image ? (
              <Box
                component="img"
                src={location.image}
                alt={location.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDark ? "warning.900" : "warning.100",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "4rem",
                    fontFamily: "'Cinzel', serif",
                    color: isDark ? "warning.600" : "warning.400",
                    opacity: 0.5,
                  }}
                >
                  {location.name.charAt(0)}
                </Typography>
              </Box>
            )}
            {/* Gradient overlay at bottom of image */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "40%",
                background: isDark
                  ? "linear-gradient(transparent, rgba(0,0,0,0.7))"
                  : "linear-gradient(transparent, rgba(255,255,255,0.9))",
              }}
            />
          </Box>

          {/* Content */}
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 0,
            }}
          >
            {/* Location Name */}
            <Typography
              level="h3"
              sx={{
                fontFamily: "'Cinzel', serif",
                fontWeight: "bold",
                lineHeight: 1.2,
                color: isDark ? "warning.200" : "warning.700",
                mb: 0.5,
              }}
            >
              {location.name}
            </Typography>

            {/* Realm/Affiliation */}
            {location.realm && (
              <Typography
                level="body-sm"
                sx={{
                  fontFamily: "'Cinzel', serif",
                  color: isDark ? "neutral.400" : "neutral.500",
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                  fontSize: "0.75rem",
                }}
              >
                {location.realm}
              </Typography>
            )}

            {/* Divider */}
            <Box
              sx={{
                width: "60px",
                height: "2px",
                bgcolor: isDark ? "warning.700" : "warning.300",
                my: 2,
              }}
            />

            {/* Description */}
            <Typography
              level="body-md"
              sx={{
                fontFamily: "'EB Garamond', serif",
                lineHeight: 1.8,
                letterSpacing: "0.02em",
                color: isDark ? "neutral.200" : "neutral.700",
              }}
            >
              {location.description}
            </Typography>

            {/* View on Map Button */}
            {location.mapCoordinates && (
              <Button
                startDecorator={<MapIcon />}
                onClick={handleViewOnMap}
                variant="soft"
                color="warning"
                size="lg"
                sx={{
                  mt: 3,
                  alignSelf: "flex-start",
                  fontFamily: "'Cinzel', serif",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                View on Map
              </Button>
            )}
          </Box>
        </Sheet>
      </Modal>
    </>
  );
};

export default HatchlingLocationName;
